import { useCallback, useState } from "react";

import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MUISelect,
} from "@mui/material";

import type { SelectProps } from "@mui/material";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

interface SelectOption {
  value: string;
  label: string;
}

export interface Props {
  inputLabel: string;
  defaultValue: string[];
  options: SelectOption[];
  selectProps?: SelectProps;
  onChange: (value: string[]) => void;
  disabled?: boolean;
  value?: string[];
  width?: string | number;
  size?: "small" | "medium";
}

export function MultiSelect(props: Props) {
  const {
    defaultValue,
    inputLabel,
    options,
    selectProps = {},
    onChange,
    disabled = false,
    value: initialValue = null,
    width = 250,
    size = "medium",
  } = props;

  // Use controlled value if provided, otherwise use internal state
  const [internalValue, setInternalValue] = useState<string[]>(
    initialValue ?? defaultValue,
  );

  const value = initialValue ?? internalValue;

  const handleChange = useCallback(
    (newValue: string[]) => {
      // Only update internal state if not controlled
      if (initialValue === undefined) {
        setInternalValue(newValue);
      }
      onChange(newValue);
    },
    [onChange, initialValue],
  );

  const getRenderLabel = (selected: string[]) => {
    const labels: string[] = [];
    (selected || []).forEach((selection) => {
      const label = options.find((o) => o.value === selection)?.label;
      if (label) {
        labels.push(label);
      }
    });
    return labels.join(", ");
  };

  return (
    <FormControl sx={{ width }} size={size}>
      <InputLabel>{inputLabel}</InputLabel>
      <MUISelect
        {...selectProps}
        label={inputLabel}
        value={value || []}
        onChange={(e) => {
          handleChange(e.target.value as string[]);
        }}
        disabled={disabled}
        multiple
        renderValue={(selected) => getRenderLabel(selected as string[])}
        MenuProps={MenuProps}
      >
        {options.map((o) => (
          <MenuItem key={o.value.toString()} value={o.value.toString()}>
            <Checkbox checked={(value || []).includes(o.value.toString())} />
            <ListItemText primary={o.label.toString()} />
          </MenuItem>
        ))}
      </MUISelect>
    </FormControl>
  );
}
